// ReportSection — copied from Protection Analytics, minus the customize-mode
// machinery, so the title is display-only.
import { type ReactNode } from 'react'
import { CanvasSpaceTitle, Tooltip } from '@clickguard/design-system'
import { InfoCircle } from 'components/icons'

// Figma elevation — outer tray + inner white card.
const OUTER_SHADOW =
	'0px 4px 16px -2px rgba(16,24,40,0.03), 0px 1px 4px -2px rgba(16,24,40,0.02)'
const INNER_SHADOW =
	'0px 4px 8px rgba(16,24,40,0.03), 0px 1px 2px rgba(16,24,40,0.02)'

interface ReportSectionProps {
	/** Optional leading 16px icon (gray). Pass an Untitled UI icon. */
	icon?: ReactNode
	/** Section title, shown via CanvasSpaceTitle. */
	title: string
	/** Info-icon tooltip copy, shown on hover/focus in the DS Tooltip. */
	info?: ReactNode
	/** Secondary tooltip line, in the DS Tooltip's regular-weight tier. */
	infoDescription?: ReactNode
	/** Accessible name for the info icon (defaults to a plain-text `info`). */
	infoLabel?: string
	/** Optional right-aligned header content (e.g. a metric switcher). */
	headerAction?: ReactNode
	/** Extra classes on the outer tray (e.g. the dark-tray remap hook). */
	className?: string
	children: ReactNode
}

export const ReportSection = ({
	icon,
	title,
	info,
	infoDescription,
	infoLabel,
	headerAction,
	className,
	children
}: ReportSectionProps) => {
	return (
		<section
			className={`flex flex-col overflow-hidden rounded-xl border border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-canvas))]${
				className ? ` ${className}` : ''
			}`}
			style={{ boxShadow: OUTER_SHADOW }}
		>
			{/* Header on the gray tray. */}
			<div className="flex items-center gap-1 py-1.5 px-1.5">
				{/* CanvasSpaceTitle's block carries cg-px-2 (it reads as a hover pill
				    in the DS); strip its right padding so the info icon sits close. */}
				<div className="flex min-w-0 items-center gap-[2px] [&>div>:first-child]:!pr-0">
					{icon && (
						<span className="flex h-4 w-4 shrink-0 items-center justify-center text-[rgb(var(--cg-text-tertiary))] [&>svg]:h-4 [&>svg]:w-4">
							{icon}
						</span>
					)}
					<CanvasSpaceTitle value={title} editable={false} size="md" />
				</div>
				{info && (
					// Same help affordance as the Automated Protection card: DS Tooltip,
					// portaled so the tray's overflow-hidden cannot clip it.
					<Tooltip
						content={info}
						description={infoDescription}
						position="top"
						showArrow
						portal
					>
						<span
							tabIndex={0}
							role="img"
							aria-label={
								infoLabel ?? (typeof info === 'string' ? info : undefined)
							}
							// 12px, the size the DS draws its own help glyph at. Resting
							// colour is the neutral icon gray at 70% — the step between
							// icon-secondary and icon-disabled, taken as alpha because
							// nothing live should wear the disabled token. `cg-shadow-focus-sm`
							// is the DS ring, so focus does not fall back to Chrome's blue.
							className="flex h-3 w-3 shrink-0 cursor-help items-center justify-center rounded-full text-[rgb(var(--cg-icon-secondary)_/_0.7)] transition-colors hover:text-[rgb(var(--cg-icon-secondary))] focus-visible:cg-shadow-focus-sm focus-visible:outline-none [&>svg]:h-3 [&>svg]:w-3"
						>
							<InfoCircle />
						</span>
					</Tooltip>
				)}
				{/* Right-aligned header action (e.g. the metric switcher). */}
				{headerAction && (
					<div className="ml-auto flex items-center gap-2">{headerAction}</div>
				)}
			</div>
			{/* White content card: own border-t + rounded + shadow, set off from the
			    tray by a hairline so the two borders read as two distinct lines. */}
			<div>
				<div
					className="overflow-hidden rounded-xl border-t border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-surface))]"
					style={{ boxShadow: INNER_SHADOW }}
				>
					{children}
				</div>
			</div>
		</section>
	)
}
